import React, { useState } from 'react';
import { VonChatFilters } from '../types';

interface PrecisionSearchInputProps {
  filters: VonChatFilters;
  onFiltersChange: (filters: Partial<VonChatFilters>) => void;
}

export const PrecisionSearchInput: React.FC<PrecisionSearchInputProps> = ({
  filters,
  onFiltersChange
}) => {
  const [localValue, setLocalValue] = useState(filters.precisionSearchQuery || '');
  const [isExpanded, setIsExpanded] = useState(!!filters.precisionSearchQuery);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = localValue.trim();
    onFiltersChange({
      precisionSearchQuery: trimmedValue || undefined
    });
  };

  const handleClear = () => {
    setLocalValue('');
    onFiltersChange({
      precisionSearchQuery: undefined
    });
    setIsExpanded(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      setLocalValue(filters.precisionSearchQuery || '');
      if (!filters.precisionSearchQuery) {
        setIsExpanded(false);
      }
    }
  };

  const isPrecisionMode = !!filters.precisionSearchQuery;

  return (
    <div className="space-y-3">
      {/* Precision Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">Precision Mode</span>
          {isPrecisionMode && (
            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
              ACTIVE
            </span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`text-xs px-2 py-1 rounded transition-colors ${
            isExpanded 
              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Precision Search Input */}
      {isExpanded && (
        <div className="space-y-3">
          <form onSubmit={handleSubmit}>
            <div className="space-y-2">
              <textarea
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter specific search terms for context retrieval..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
              />
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    Apply
                  </button>
                  {(localValue || filters.precisionSearchQuery) && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  Enter to apply, Esc to cancel
                </span>
              </div>
            </div>
          </form>

          {/* Current Precision Query Display */}
          {isPrecisionMode && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <div className="mt-0.5 w-2 h-2 bg-purple-500 rounded-full"></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-purple-800">
                    Active Precision Query
                  </div>
                  <div className="text-sm text-purple-700 mt-1 break-words">
                    "{filters.precisionSearchQuery}"
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="text-xs text-gray-500 space-y-1">
            <div className="flex items-center space-x-2">
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span>Use specific terms to find targeted context</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span>Different from your chat message - focuses context retrieval</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span>Leave empty for automatic context detection</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 